/**
 * Wizard step list, shared by WizardPage, the sidebar and the layout.
 *
 * `current_step` is persisted server-side as a plain integer index, and
 * `total_steps` is a static value on the backend handler
 * (`CASHFLOW_TOTAL_STEPS` in backend/service_client_forms/forms/cashflow.py).
 *
 * The Import step was inserted at index 1 (steps version 2), which moved every
 * later step down by one. Progress saved before that has no
 * `form_data._steps_version`, and `migrateProgress` shifts its saved index so
 * the client reopens on the same section. Append new steps from here on —
 * inserting or reordering needs another version bump and migration.
 *
 * `number` is the section number shown in the sidebar; it matches each
 * step's SectionHeader. Steps without one show their icon instead.
 */
export const STEPS_VERSION = 2;

export const STEPS = [
  { key: 'welcome',              label: 'Welcome',                icon: 'bi-house-heart' },
  { key: 'import',               label: 'Import Statements',      icon: 'bi-cloud-arrow-up' },
  { key: 'income',               label: 'Income',                 icon: 'bi-cash-coin',         number: 1 },
  { key: 'housing',              label: 'Housing',                icon: 'bi-house',             number: 2 },
  { key: 'bills_utilities',      label: 'Bills & Utilities',      icon: 'bi-plug',              number: 3 },
  { key: 'auto_transport',       label: 'Auto & Transport',       icon: 'bi-car-front',         number: 4 },
  { key: 'medical',              label: 'Medical',                icon: 'bi-heart-pulse',       number: 5 },
  { key: 'insurance',            label: 'Insurance',              icon: 'bi-shield-check',      number: 6 },
  { key: 'entertainment_travel', label: 'Entertainment & Travel', icon: 'bi-airplane',          number: 7 },
  { key: 'personal_family_care', label: 'Personal & Family',      icon: 'bi-people',            number: 8 },
  { key: 'kids_education',       label: 'Kids & Education',       icon: 'bi-mortarboard',       number: 9 },
  { key: 'pets',                 label: 'Pets',                   icon: 'bi-emoji-heart-eyes',  number: 10 },
  { key: 'charity',              label: 'Charity',                icon: 'bi-gift',              number: 11 },
  { key: 'legal_professional',   label: 'Legal / Professional',   icon: 'bi-briefcase',         number: 12 },
  { key: 'savings',              label: 'Savings',                icon: 'bi-piggy-bank',        number: 13 },
  { key: 'debts',                label: 'Debts',                  icon: 'bi-credit-card',       number: 14 },
  { key: 'review',               label: 'Review & Submit',        icon: 'bi-check2-all',        number: 15 },
];

/** Steps that collect no section data and so never offer "Skip". */
export const NON_SKIPPABLE = new Set(['welcome', 'import', 'review']);

/** Index of the step with this key (-1 when unknown). */
export function stepIndex(key) {
  return STEPS.findIndex((step) => step.key === key);
}

/**
 * Bring saved progress up to the current step layout.
 *
 * Returns the step index to open and form data stamped with STEPS_VERSION.
 * Version-1 progress (before the Import step existed) is shifted by one
 * from Income onwards; Welcome (0) stays put.
 */
export function migrateProgress(savedStep, formData) {
  const data = formData || {};
  const step = Math.max(0, Number(savedStep) || 0);
  if ((data._steps_version || 1) >= STEPS_VERSION) {
    return { step: Math.min(step, STEPS.length - 1), formData: data };
  }
  const shifted = step >= 1 ? step + 1 : step;
  return {
    step: Math.min(shifted, STEPS.length - 1),
    formData: { ...data, _steps_version: STEPS_VERSION },
  };
}

/**
 * Indices of the steps the client doesn't see. The Import step is hidden
 * while the server accepts no document types (`import_types` is an empty
 * list); undefined — an older server — keeps it. STEPS itself never changes,
 * because saved progress is an index into it.
 */
export function hiddenSteps(importTypes) {
  const noImport = Array.isArray(importTypes) && importTypes.length === 0;
  return new Set(noImport ? [stepIndex('import')] : []);
}

/** The next step the client sees after `current` (`current` itself at the end). */
export function nextStep(current, hidden = new Set()) {
  for (let i = current + 1; i < STEPS.length; i += 1) {
    if (!hidden.has(i)) return i;
  }
  return current;
}

/** The step the client sees before `current` (`current` itself at the start). */
export function prevStep(current, hidden = new Set()) {
  for (let i = current - 1; i >= 0; i -= 1) {
    if (!hidden.has(i)) return i;
  }
  return current;
}

/** `step`, or the next step the client sees when it is hidden (progress saved on Import). */
export function visibleStep(step, hidden = new Set()) {
  return hidden.has(step) ? nextStep(step, hidden) : step;
}
